import { useEffect, useMemo } from 'react'
import { z } from 'zod'
import { useQuery } from 'convex/react'
import { api } from '@backend/_generated/api'
import type { Id } from '@backend/_generated/dataModel'

import type { AnyReactFormApi } from '@/types/form'

export interface UseTrajectoryPathManagerProps {
  positionId: string
  form: AnyReactFormApi
}

export interface UseTrajectoryPathManagerReturn {
  trajectories: Array<{ id: string; path: string }>
  isLoading: boolean
  hasMultipleTrajectories: boolean
  singleTrajectory: { id: string; path: string } | null
  validateTrajectoryPath: (value: string) => string | undefined
}

const TRAJECTORY_PATH_SCHEMA = z.string().min(1, 'Trajectory path is required')

/**
 * Custom hook to manage trajectory path selection and validation
 *
 * Fetches available trajectory paths for a position via the `startingPositionTrajectories`
 * include on the position detail endpoint, then deduplicates by trajectoryId.
 *
 * @param positionId - The ID of the position to fetch trajectories for
 * @param form - The form instance from TanStack Form
 * @returns Trajectory data, loading state, and validation function
 */
export function useTrajectoryPathManager({
  positionId,
  form,
}: UseTrajectoryPathManagerProps): UseTrajectoryPathManagerReturn {
  const rows = useQuery(
    api.positions.listStartingTrajectories,
    positionId ? { positionId: positionId as Id<'positions'> } : 'skip',
  )

  const isLoading = rows === undefined

  // Extract unique { id: trajectoryId, path } pairs
  const trajectories = useMemo(() => {
    if (!rows) return []
    const seen = new Set<string>()
    const result: Array<{ id: string; path: string }> = []
    for (const item of rows) {
      if (!seen.has(item.trajectoryId)) {
        seen.add(item.trajectoryId)
        result.push({ id: item.trajectoryId, path: item.path })
      }
    }
    return result
  }, [rows])

  const hasMultipleTrajectories = trajectories.length > 1
  const singleTrajectory = trajectories.length === 1 ? trajectories[0] : null

  // Handle path updates when trajectories load or position changes
  useEffect(() => {
    if (!positionId || isLoading) return

    const currentPath = form.state.values.path

    // Clear stale path that doesn't belong to current position
    if (currentPath && trajectories.length > 0) {
      const isValidPath = trajectories.some((t) => t.path === currentPath)
      if (!isValidPath) {
        form.setFieldValue('path', '')
        return
      }
    }

    // Auto-select single trajectory
    if (singleTrajectory && currentPath !== singleTrajectory.path) {
      form.setFieldValue('path', singleTrajectory.path)
    }
  }, [positionId, isLoading, trajectories, singleTrajectory, form])

  const validateTrajectoryPath = (value: string): string | undefined => {
    // Block submission while trajectories are loading
    if (positionId && isLoading) {
      return 'Loading trajectory paths...'
    }
    // Validate with schema
    return TRAJECTORY_PATH_SCHEMA.safeParse(value).success
      ? undefined
      : 'Trajectory path is required'
  }

  return {
    trajectories,
    isLoading,
    hasMultipleTrajectories,
    singleTrajectory,
    validateTrajectoryPath,
  }
}
